import './delivery-address.css';

import { CustomDatalist } from "../../../utils/custom-datalist";
import { ITempAddress } from "../../../interfaces/temp.interface";
import { Data } from "../../../models/data";
import { IUserAddress } from "../../../models/user.interface";
import { IAddressZone, IParseAddressGetCity, IParseAddressGetState, IParseAddressGetZip } from "../../../online-ordering/interfaces/online-ordering.interface";
import { IAddressParsed } from "../../../utils/util.interface";
import { AddressService } from "../../../services/address-service";
import { LocationService } from "../../../services/location-service";
import { ConfirmButtonState, IDeliveryAddress, IDeliveryAddressReferences, IFillInAddress } from "./delivery-address.interface";
import { AddressLookupType, AddressType, AddressValidation, AutocompleteProvider, Country } from "../../../types/enums";
import { DialogCreators } from "../../../utils/dialog-creators";
import { TwoWayBindingHelper } from "../../../utils/two-way-binding";
import { Util } from "../../../utils/util";
import { SuggestionsAnnouncer } from "../../../utils/suggestions-announcer";
import { LocalizationService } from "../../../services/localization-service";
import { IStoreLocation } from "../../../models/data.interface";
import { Order } from "../../../models/order";
import { DataLayerService } from "../../../services/data-layer-service";
import { ValidationDOMHelper } from "../../../validators/validation-dom-helper";
import { AddressResponse } from "../../../services/address-service.interface";
import { InputValidator } from "../../../validators/input-validator";
import { LatLng } from "../../../services/maps/map-provider.interface";
import { GeoAddress } from "../../../services/geo-search/address-search-provider.interface";
import { GeoSearchSelection } from "../../../services/geo-search/geo-search-provider-factory";
import { getCountryCentroid } from "../../../services/geo-search/country-centroid";
import { AddressSuggestBox } from "../AddressSuggestBox/address-suggest-box";

export class DeliveryAddress {
    private readonly _data: Data;
    private readonly _localizationService: LocalizationService;
    private readonly _locationService: LocationService;
    private readonly _addressService: AddressService;
    private readonly _dataLayerService: DataLayerService;
    private readonly _item: IDeliveryAddress;
    private readonly _containerReferences: IDeliveryAddressReferences;
    private readonly _containerElement: HTMLElement;
    private readonly _bindingHelper: TwoWayBindingHelper<IDeliveryAddress>;
    private readonly _order: Order;
    private readonly _selectAddressFunc: (status: AddressValidation, storeKey: string, address: ITempAddress | null) => void;
    private readonly _suggestionsAnnouncer: SuggestionsAnnouncer;
    private readonly _isSingleStoreScoped: boolean;
    private _addressSuggestBox: AddressSuggestBox | null = null;

    constructor(
        data: Data,
        order: Order,
        localizationService: LocalizationService,
        locationService: LocationService,
        addressService: AddressService,
        dataLayerService: DataLayerService,
        geoSearch: GeoSearchSelection,
        locationId: string,
        isSingleStoreScoped: boolean,
        selectAddressFunc: (status: AddressValidation, storeKey: string, address: ITempAddress | null) => void
    ) {
        this._data = data;
        this._localizationService = localizationService;
        this._locationService = locationService;
        this._addressService = addressService;
        this._dataLayerService = dataLayerService;
        this._isSingleStoreScoped = isSingleStoreScoped;
        this._suggestionsAnnouncer = new SuggestionsAnnouncer(() => this._localizationService.names("AutoSuggestionsAvailable"));

        this._item = this._createModel(locationId, geoSearch);
        this._containerElement = this._createHtmlContainer();
        this._containerReferences = this._createReferences();
        this._bindingHelper = new TwoWayBindingHelper(this._item);
        this._setTwoWayListeners();
        this._selectAddressFunc = selectAddressFunc;
        this._order = order;
    }

    /**
     * Gets the HTML container element for the schedule later component.
     */
    public get element(): HTMLElement {
        return this._containerElement;
    }

    public dispose(): void {
        this._addressSuggestBox?.dispose();
        this._addressSuggestBox = null;
    }

    private _createModel(locationId: string, geoSearch: GeoSearchSelection): IDeliveryAddress {
        return {
            locationId: locationId,
            locations: [],
            randomId: 0,
            selectedStoreKey: "",
            selectedAddress: null,
            streetAddress: "",
            apartment: "",
            deliveryInstructions: "",
            geoSearch
        };
    }

    private readonly _createReferences = (): IDeliveryAddressReferences => {
        return {
            streetAddress: this._containerElement.querySelector(`#txt_${this._item.locationId}_delivery_address`) as HTMLInputElement,
            datalist: this._containerElement.querySelector(`#dl_${this._item.locationId}_delivery_address`) as HTMLDataListElement,
            datalistWv: this._containerElement.querySelector(`#dl_${this._item.locationId}_delivery_address_wv`) as HTMLUListElement,
            apartment: this._containerElement.querySelector(`#txt_${this._item.locationId}_delivery_apartment`) as HTMLInputElement,
            deliveryInstructions: this._containerElement.querySelector(`#txt_${this._item.locationId}_delivery_instructions`) as HTMLInputElement
        };
    }

    private _createHtmlContainer(): HTMLElement {
        const html = `
            <div id="div_${this._item.locationId}_address_form_content" class="address-form-content">
                <div id="txt_${this._item.locationId}_delivery_apartment_limited_time" class="address-form-out-of-limited-time hidden" ltag="DeliveryOutLimitedTimeError">${this._localizationService.names("DeliveryOutLimitedTimeError")}</div>

                <div class="address-form-input-section">
                    <label for="txt_${this._item.locationId}_delivery_address" class="form-label"><span ltag="Address">${this._localizationService.names("Address")}</span> <span class="required">*</span></label>
                    <input type="text" id="txt_${this._item.locationId}_delivery_address" ltagp="StreetAddressPlaceholder" placeholder="${this._localizationService.names("StreetAddressPlaceholder")}" list="dl_${this._item.locationId}_delivery_address" autocomplete="off" aria-describedby="spn_${this._item.locationId}_delivery_address_error" aria-invalid="false" role="combobox" aria-expanded="false" aria-controls="dl_${this._item.locationId}_delivery_address_wv" aria-autocomplete="list" aria-haspopup="listbox" />
                    <datalist id="dl_${this._item.locationId}_delivery_address"></datalist>
                    <ul id="dl_${this._item.locationId}_delivery_address_wv"></ul>
                    <p id="spn_${this._item.locationId}_delivery_address_error" class="error-text hidden" role="alert" aria-live="polite" ltag="DeliveryAddressIncompleteAddressError">${this._localizationService.names("DeliveryAddressIncompleteAddressError")}</p>
                </div>

                <div class="address-form-input-section">
                    <label for="txt_${this._item.locationId}_delivery_apartment" class="form-label">
                        <span ltag="AptSuiteRoom">${this._localizationService.names("AptSuiteRoom")}</span> <span class="optional">(<span ltag="Optional">${this._localizationService.names("Optional")}</span>)</span>
                    </label>
                    <input type="text" id="txt_${this._item.locationId}_delivery_apartment" maxlength="50" ltagp="AptSuiteRoomPlaceholder" placeholder="${this._localizationService.names("AptSuiteRoomPlaceholder")}"/>
                </div>

                <div class="address-form-input-section">
                    <label for="txt_${this._item.locationId}_delivery_instructions" class="form-label">
                        <span ltag="DeliveryInstructions">${this._localizationService.names("DeliveryInstructions")}</span> <span class="optional">(<span ltag="Optional">${this._localizationService.names("Optional")}</span>)</span>
                    </label>
                    <input type="text" id="txt_${this._item.locationId}_delivery_instructions" maxlength="100" ltagp="DeliveryInstructionsPlaceholder" placeholder="${this._localizationService.names("DeliveryInstructionsPlaceholder")}" />
                </div>
                <div class="address-form-button-section">
                    <button id="btn_${this._item.locationId}_start_order" ltag="StartOrder">${this._localizationService.names("StartOrder")}</button>
                </div>
            </div>
        `;

        return Util.createHtmlElementFromTemplate(html);
    }

    private readonly _setTwoWayListeners = (): void => {
        this._bindingHelper.bindToInput("streetAddress", `txt_${this._item.locationId}_delivery_address`, this._containerElement);
        this._bindingHelper.bindToInput("apartment", `txt_${this._item.locationId}_delivery_apartment`, this._containerElement);
        this._bindingHelper.bindToInput("deliveryInstructions", `txt_${this._item.locationId}_delivery_instructions`, this._containerElement);
    }

    public async initAsync(locations: IStoreLocation[]): Promise<void> {
        this._item.locations = locations;

        this._renderPage();
        this._setEventListeners();
    }

    // An unsupported primary (Mapbox, Geocodio) resolves to NONE but can still have a failover
    // in the chain, so the chain decides — only Adora sits outside it.
    private _renderPage(): void {
        if (this._item.geoSearch.providerId === AutocompleteProvider.ADORA) {
            this._initializeAdoraAutocomplete();
            return;
        }

        this._initializeAddressSuggestBox();
    }

    private readonly _setEventListeners = (): void => {
        this._containerReferences.streetAddress.onfocus = () => {
            this._hideAddressErrors();
        };

        this._containerReferences.streetAddress.onblur = () => {
            if (!this._isAddressComplete(this._item.selectedAddress)) {
                this._item.selectedStoreKey = "";
                this._item.selectedAddress = null;
                this._selectAddressFunc(AddressValidation.INVALID, "", this._item.selectedAddress);
            }
        };

        // BUG 45761: type-in non existing delivery address causes last correct address to show up on checkout -> making address invalid & forcing user to select address from the list
        // Use addEventListener (not oninput =) so this doesn't overwrite the Adora autocomplete's
        // oninput handler that was set in _initializeAdoraAutocomplete during _renderPage.
        this._containerReferences.streetAddress.addEventListener("input", () => {
            if (this._item.selectedAddress) {
                this._selectAddressFunc(AddressValidation.INVALID, "", this._item.selectedAddress);
            }
        });
    }

    private readonly _hideAddressErrors = (): void => {
        ValidationDOMHelper.clearErrorState(this._containerReferences.streetAddress, `spn_${this._item.locationId}_delivery_address_error`);
    }

    private _isAddressComplete(address: ITempAddress | null): boolean {
        if (address?.CountryID === Country.BAHAMAS) // Exclude Address validation for Bahamas
            return true;

        if (address?.StreetNo
            && address?.StreetName
            && address?.City
            && address?.State
            && address?.Zip)
            return true;
        return false;
    }

    public setConfirmButton(localizationKey: string, onClick: () => void): void {
        const button = this._containerElement.querySelector(`#btn_${this._item.locationId}_start_order`) as HTMLButtonElement;
        if (button) {
            button.setAttribute("ltag", localizationKey);
            button.textContent = this._localizationService.names(localizationKey);
            button.onclick = onClick;
        }
    }

    public setConfirmButtonState(state: ConfirmButtonState): void {
        const button = this._containerElement.querySelector(`#btn_${this._item.locationId}_start_order`) as HTMLButtonElement;
        if (button) {
            button.disabled = state === ConfirmButtonState.DISABLED;
        }
    }

    public clearStreetAddress(): void {
        this._containerReferences.streetAddress.value = "";
    }

    public readonly checkDeliveryAddressAsync = async (userAddress: IUserAddress, displayError: boolean = true, targetStoreKey?: string): Promise<void> => {
        if (this._data.getBrandProperty("Settings").ADDRLOOKUP === AddressLookupType.NONE)
            return;

        const tempAddress = Util.getTempAddressFromUserAddress(userAddress);

        if (targetStoreKey) {
            const store = this._item.locations.find(x => x.storeKey === targetStoreKey);
            if (!store) {
                this._selectAddressFunc(AddressValidation.OUT_OF_DELIVERY_ZONE, "", tempAddress);
                return;
            }
            if (store.zones.length === 0)
                await this._getStoreDeliveryZonesAsync([store.storeId]);
            this._checkIfAddressIsInZone([store], tempAddress, displayError);
            return;
        }

        const closestStores = await this._getClosestStoresToAddressAsync(tempAddress.Latitude, tempAddress.Longitude);
        this._checkIfAddressIsInZone(closestStores, tempAddress, displayError);
    }

    public getDeliveryAddressInfo(): { storeKey: string, address: ITempAddress | null } {
        if (this._item.selectedAddress) {
            this._item.selectedAddress.DeliveryInstruction = this._item.deliveryInstructions;

            if (this._item.selectedAddress.AddressID === 0) {
                this._item.selectedAddress.AddressTypeID = this._item.apartment ? AddressType.APT : AddressType.HOUSE;
                this._item.selectedAddress.Address2 = this._item.apartment;
            }
        }

        return {
            storeKey: this._item.selectedStoreKey,
            address: this._item.selectedAddress
        };
    }

    /**
     * Initializes Adora Autocomplete for the delivery address input field.
     * Adds an event listener to populate suggestions and update the selected address based on user input.
     */
    private readonly _initializeAdoraAutocomplete = (): void => {
        const txtAddress = this._containerReferences.streetAddress;

        txtAddress.oninput = async () => {
            this._item.selectedStoreKey = "";
            this._item.selectedAddress = null;
            this._selectAddressFunc(AddressValidation.INVALID, "", this._item.selectedAddress);

            const allZipCodes = this._item.locations.flatMap(x => x.zipCodes);
            const uniqueZipCodes = Array.from(new Set(allZipCodes));
            await this._updateAddressList(uniqueZipCodes.join(','), null);
        };

        txtAddress.addEventListener('input', async (e) => {
            await this._handleDatalistSelectionAsync(e as InputEvent)
        });
    }

    /**
    * Asynchronously updates the address list displayed in a specified data list element based on user input.
    * @async
    * @param {any} callback - Optional callback function to execute before updating the address list.
    * @returns {Promise<void>} - Promise that resolves when the data list element has been updated.
    */
    private async _updateAddressList(zipCodes: string, callback: Function | null): Promise<void> {
        const input = this._item.streetAddress;
        if (!input) {
            this._containerReferences.datalist.innerHTML = "";
            this._suggestionsAnnouncer.update(false);
            return;
        }

        if (input.length > 3 && input.length < 21) {
            const data = {
                addr: input,
                zips: zipCodes
            };
            const result = await this._addressService.getAddressList(data);
            this._setAddressList(result);
        } else if (input.length < 4) {
            this._containerReferences.datalist.innerHTML = "";
            this._suggestionsAnnouncer.update(false);
        } else if (callback)
            await callback(input);
    }

    /**
    * Updates the address options displayed in a specified data list element based on the response from an endpoint.
    * @param {string} response - The response from the endpoint as a string.
    * @returns {void}
    */
    private _setAddressList(response: { Addr: any[] }): void {
        const dataList = Util.isAppView() ? this._containerReferences.datalistWv : this._containerReferences.datalist;

        if (response.Addr.length == 0) {
            dataList.innerHTML = "";
            this._suggestionsAnnouncer.update(false);
            return;
        }

        const addrs = response;
        if (!addrs.Addr || !dataList)
            return;

        this._suggestionsAnnouncer.update(true);

        const addList: string[] = [];
        const dlItems: { text: string, value: string }[] = [];
        for (const item of addrs.Addr) {
            let addr: string = item.StNo;
            if (item.StPreDirAbbr !== "") { addr += ' ' + item.StPreDirAbbr; }
            if (item.StName !== "") { addr += ' ' + Util.FixStringCase(item.StName); }
            if (item.StSuffixAbbr !== "") { addr += ' ' + Util.FixStringCase(item.StSuffixAbbr); }
            if (item.StPostDirAbbr !== "") { addr += ' ' + item.StPostDirAbbr; }
            if (item.City !== "") { addr += ' ' + item.City; }
            if (item.State !== "") { addr += ' ' + item.State; }
            if (item.ZipCode !== "") { addr += ' ' + item.ZipCode; }

            addList.push(addr);

            if (Util.isAppView()) {
                dlItems.push({ text: addr, value: JSON.stringify(item) });
            } else {
                const found = Array.from(dataList.children).some(child => (child as HTMLOptionElement).value === addr);

                if (!found) {
                    const option = document.createElement("option");
                    option.value = addr;
                    option.setAttribute("data-value", JSON.stringify(item));
                    option.onclick = () => {
                        this._containerReferences.apartment.focus();
                    };
                    dataList.appendChild(option);
                }
            }
        }

        if (Util.isAppView()) {
            new CustomDatalist(
                this._containerReferences.streetAddress.id,
                dataList.id,
                dlItems
            );

            dataList.querySelectorAll("div").forEach((child) => {
                if (!addList.includes(child.innerText))
                    child.remove();
            });
        } else {
            while (dataList.children.length > addList.length) {
                for (let i = 0; i < dataList.children.length; i++) {
                    if (!addList.includes((dataList.children[i] as HTMLOptionElement).value)) {
                        dataList.children[i].remove();
                        break;
                    }
                }
            }
        }
    }

    /**
     * Handles selection of an address option from the datalist.
     * If a valid option is selected, it updates the address fields.
     *
     * @param {InputEvent} e - The input event triggered by the user.
     */
    private readonly _handleDatalistSelectionAsync = async (e: InputEvent): Promise<void> => {
        const input = e.target as HTMLInputElement;
        if (!input)
            return;

        const listId = input.getAttribute('list');
        if (!listId)
            return;

        const datalist = document.getElementById(listId);
        if (!datalist)
            return;

        const options = Array.from(datalist.querySelectorAll('option'));
        const inputValue = input.value;

        Util.hideElement(`txt_delivery_apartment_limited_time_${this._item.randomId}`);

        const matched = options.find(o => o.value === inputValue);
        const address = matched?.dataset.value;

        if (address)
            await this._fillInAddressFromAdoraAsync(JSON.parse(address));
    }

    /**
     * Fills in the address fields based on the selected address item from Adora.
     * Verifies the address with Google Geocoder and fills in the parsed address information.
     *
     * @param {IFillInAddress} item - The address item selected from the datalist.
     */
    private async _fillInAddressFromAdoraAsync(item: IFillInAddress): Promise<void> {
        const addressString = this._constructAddressString(item);

        const streetName = this._constructStreetName(item);
        const adoraAddress: IAddressParsed = {
            StreetNo: item.StNo,
            StreetName: streetName,
            StreetShortName: streetName,
            City: item.City,
            State: item.State,
            Zip: item.ZipCode,
            Latitude: item.Latitude,
            Longitude: item.Longitude
        };

        // Forward geocode the address text using the active provider
        const coords = await this._item.geoSearch.chain?.geocodeAsync(addressString);

        // If geocoding fails, match your old ZERO_RESULTS behavior
        if (!coords) {
            await this._fillInParsedAddress(adoraAddress, {} as any);
            return;
        }

        // 2) Optionally reverse geocode to get structured components (provider-neutral)
        const geoAddr = await this._item.geoSearch.chain?.reverseGeocodeAsync(coords);

        // 3) Convert provider-neutral GeoAddress -> whatever your verify function expects
        //    If _verifyAndFillAddress currently expects a "googleAddress" object,
        //    change it to accept a provider-neutral object instead.
        const allCities = this._item.locations.flatMap(x => x.cities);
        const uniqueCities = Array.from(new Set(allCities));

        const providerAddress = this._parseGeoAddress(geoAddr ?? null, item.StNo, item.City, uniqueCities);

        await this._verifyAndFillAddress(adoraAddress, providerAddress, item);
    }

    /**
     * Constructs a formatted address string for geocoding.
     *
     * @param {IFillInAddress} item - The address item containing individual address components.
     * @returns {string} - The constructed address string.
     */
    private readonly _constructAddressString = (item: IFillInAddress): string => {
        let address = item.StNo;
        if (item.StPreDirAbbr) address += ' ' + item.StPreDirAbbr;
        if (item.StName) address += ' ' + Util.FixStringCase(item.StName);
        if (item.StSuffixAbbr) address += ' ' + Util.FixStringCase(item.StSuffixAbbr);
        if (item.StPostDirAbbr) address += ' ' + item.StPostDirAbbr;
        if (item.City) address += ', ' + item.City;
        if (item.State) address += ', ' + item.State;
        if (item.ZipCode) address += ' ' + item.ZipCode;
        return address;
    }

    /**
     * Constructs the street name portion of the address.
     *
     * @param {IFillInAddress} item - The address item containing street information.
     * @returns {string} - The constructed street name.
     */
    private readonly _constructStreetName = (item: IFillInAddress): string => {
        return [
            item.StPreDirAbbr,
            item.StName && Util.FixStringCase(item.StName),
            item.StSuffixAbbr && Util.FixStringCase(item.StSuffixAbbr),
            item.StPostDirAbbr
        ].filter(Boolean).join(" ");
    }

    /**
     * Verifies and fills the parsed address based on Google Geocoder and Adora data.
     *
     * @param {IAddressFormat} adoraAddress - Address data from Adora.
     * @param {IAddressFormat} googleAddress - Parsed address data from Google.
     * @param {IFillInAddress} item - The selected address item from Adora.
     */
    private readonly _verifyAndFillAddress = async (adoraAddress: IAddressParsed, googleAddress: IAddressParsed, item: IFillInAddress): Promise<void> => {
        const googleStreetNameCompressed = googleAddress.StreetName.replace(/[^0-9a-z]/gi, '').toLowerCase();
        const googleStreetShortNameCompressed = googleAddress.StreetShortName.replace(/[^0-9a-z]/gi, '').toLowerCase();
        const adoraStreetNameCompressed = this._constructStreetName(item).replace(/[^0-9a-z]/gi, '').toLowerCase();

        // Compare ZIP codes or apply street name exceptions if necessary
        if (googleAddress.Zip !== item.ZipCode && this._zipException(googleAddress.Zip, item.ZipCode)) {
            googleAddress.Zip = this._zipException(googleAddress.Zip, item.ZipCode);
            await this._fillInParsedAddress(adoraAddress, googleAddress);
        } else if (googleStreetNameCompressed !== adoraStreetNameCompressed && googleStreetShortNameCompressed !== adoraStreetNameCompressed) {
            await this._handleStreetNameExceptions(adoraAddress, googleAddress, item);
        } else {
            await this._fillInParsedAddress(adoraAddress, googleAddress);
        }
    }

    /**
     * Handles street name exceptions for numbers (1st, 2nd, 3rd, etc.) by updating the address and re-validating.
     *
     * @param {IAddressFormat} adoraAddress - Address data from Adora.
     * @param {IAddressFormat} googleAddress - Parsed address data from Google.
     * @param {IFillInAddress} item - The selected address item from Adora.
     */
    private readonly _handleStreetNameExceptions = async (adoraAddress: IAddressParsed, googleAddress: IAddressParsed, item: IFillInAddress): Promise<void> => {
        const ordinalMap: Record<string, string> = {
            "1st": "First", "2nd": "Second", "3rd": "Third", "4th": "Fourth", "5th": "Fifth",
            "6th": "Sixth", "7th": "Seventh", "8th": "Eighth", "9th": "Ninth", "10th": "Tenth",
            "11th": "Eleventh", "12th": "Twelfth", "13th": "Thirteenth"
        };

        if (ordinalMap[item.StName.toLowerCase()]) {
            item.StName = ordinalMap[item.StName.toLowerCase()];
            await this._fillInAddressFromAdoraAsync(item);
        } else {
            await this._fillInParsedAddress(adoraAddress, googleAddress);
        }
    }

    /**
     * Fills in the parsed address details from primary and secondary address sources.
     * Checks if the address falls within the delivery zone and validates it accordingly.
     *
     * @param {IAddressParsed} primaryAddr - The primary address information.
     * @param {IAddressParsed} secondaryAddr - The secondary address information.
     * @returns {Promise<void>} - The validation status of the address.
     */
    private readonly _fillInParsedAddress = async (primaryAddr: IAddressParsed, secondaryAddr: IAddressParsed): Promise<void> => {
        const address = this._getTempAddressNew();
        address.StreetNo = primaryAddr.StreetNo || secondaryAddr.StreetNo || "0";
        address.StreetName = primaryAddr.StreetShortName || secondaryAddr.StreetShortName || "";
        address.City = primaryAddr.City || secondaryAddr.City || this._data.getBrandProperty("StoreInfo").city;
        address.State = primaryAddr.State || secondaryAddr.State || "";
        address.Zip = primaryAddr.Zip || secondaryAddr.Zip || "";
        address.Latitude = secondaryAddr.Latitude || primaryAddr.Latitude || 0;
        address.Longitude = secondaryAddr.Longitude || primaryAddr.Longitude || 0;

        const closestStores = await this._getClosestStoresToAddressAsync(address.Latitude, address.Longitude);
        this._checkIfAddressIsInZone(closestStores, address);
    }

    private _getTempAddressNew(): ITempAddress {
        return {
            AddressID: 0,
            AddressTypeID: AddressType.HOUSE,
            AddressName: 'Home',
            IsPrimary: true,
            StreetNo: "",
            StreetName: "",
            Address2: "",
            Address3: "",
            Address4: "",
            Address5: "",
            City: "",
            State: "",
            Zip: "",
            CountryID: null,
            Latitude: 0,
            Longitude: 0,
            XStreet: "",
            ZoneID: 0,
            Grid: "",
            Distance: 0,
            DeliveryInstruction: "",
        };
    }

    private readonly _calculateDistanceFromDeliveryAddress = (latitude: number, longitude: number): void => {
        // Calculate and update distance for each location
        for (const location of this._item.locations) {
            location.deliveryDistance = Util.Float2(
                Util.GetDistance(latitude, longitude, location.latitude, location.longitude, "M")
            );
        }

        // Sort locations by distance
        this._item.locations.sort((a: IStoreLocation, b: IStoreLocation) => a.deliveryDistance - b.deliveryDistance);
    }

    private readonly _getClosestStoresToAddressAsync = async (latitude: number, longitude: number): Promise<IStoreLocation[]> => {
        // Calculate distance of stores from delivery address and get zones for closest stores
        this._calculateDistanceFromDeliveryAddress(latitude, longitude);

        // A single-store-scoped consumer (OTAT) must check only its own store, never the
        // brand-wide search, or the order can get silently switched elsewhere.
        if (this._isSingleStoreScoped) {
            const currentStoreKey = this._data.getProperty("StoreInfo")?.storeKey;
            const currentStore = this._item.locations.find(x => x.storeKey === currentStoreKey);
            const scopedStores = currentStore ? [currentStore] : [];
            await this._getStoreDeliveryZonesAsync(scopedStores.filter(x => x.zones.length == 0).map(x => x.storeId));
            return scopedStores;
        }

        const closestStores = this._item.locations.slice(0, 4);
        // Get store zones only if location has no zones
        await this._getStoreDeliveryZonesAsync(closestStores.filter(x => x.zones.length == 0).map(x => x.storeId));
        return closestStores;
    }

    private readonly _getStoreDeliveryZonesAsync = async (storeIds: number[]): Promise<void> => {
        if (storeIds.length == 0)
            return;

        const deliveryZones = await this._locationService.loadDeliveryZonesForLocations(storeIds);
        for (const store of deliveryZones) {
            const location = this._item.locations.find(x => x.storeKey === store.storeKey);
            if (location)
                location.zones = store.zones;
        }
    }

    private readonly _checkIfAddressIsInZone = (
        stores: IStoreLocation[],
        address: ITempAddress,
        displayError: boolean = true
    ): void => {
        const orderTime = this._locationService.resolveOrderTime(
            this._order.getProperty("FutureDate"),
            this._order.getProperty("FutureMinute"),
            this._data.getProperty("TimeOffset")
        );

        const result = this._locationService.findDeliveryZoneForAddress(
            address.Latitude,
            address.Longitude,
            stores,
            orderTime
        );

        if (result.status === AddressValidation.OUT_OF_DELIVERY_ZONE) {
            if (displayError) {
                DialogCreators.messageBoxOk(
                    this._localizationService,
                    this._dataLayerService,
                    this._localizationService.names("DeliveryAddressOutsideAllZonesError"),
                    true
                );
            }
            this._item.selectedStoreKey = "";
            this._item.selectedAddress = null;
            this._selectAddressFunc(AddressValidation.OUT_OF_DELIVERY_ZONE, "", address);
            return;
        }

        if (result.status === AddressValidation.OUT_OF_TIME) {
            Util.showElement(`txt_delivery_apartment_limited_time_${this._item.randomId}`);
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayerService,
                this._localizationService.names("OutTimeDelivery"),
                true
            );
        }

        // Both VALID and OUT_OF_TIME proceed — the address is geographically in-zone.
        let newTempAddress = this._createTempAddress(address, result.zoneId);
        if (address.AddressID !== 0) {
            address.ZoneID = result.zoneId;
            newTempAddress = address;
        }

        if (document.getElementById(`div_${this._item.locationId}_address_form_content`)
            ?.classList.contains("open"))
            this._containerReferences.apartment.focus();

        this._item.selectedStoreKey = result.storeKey;
        this._item.selectedAddress = newTempAddress;
        this._selectAddressFunc(result.status, result.storeKey, newTempAddress);
    }

    /**
     * Creates a temporary address based on provided address details and zone ID.
     *
     * @param {object} details - The address details to populate the temporary address.
     * @param {number} zoneId - The zone ID for the delivery area.
     * @returns {IAddressParsed} - The newly created temporary address object.
     */
    private readonly _createTempAddress = (details: ITempAddress, zoneId: number): ITempAddress => {
        const aptNum = this._item.apartment;

        return {
            AddressID: 0,
            AddressTypeID: aptNum ? AddressType.APT : AddressType.HOUSE,
            AddressName: 'Home',
            IsPrimary: true,
            StreetNo: details.StreetNo,
            StreetName: details.StreetName,
            Address2: details.Address2 || aptNum,
            Address3: details.Address3 || "",
            Address4: details.Address4 || "",
            Address5: details.Address5 || "",
            City: details.City,
            State: details.State,
            Zip: details.Zip,
            CountryID: details.CountryID || null,
            Latitude: details.Latitude,
            Longitude: details.Longitude,
            XStreet: "",
            ZoneID: zoneId,
            Grid: "",
            Distance: 0,
            DeliveryInstruction: this._item.deliveryInstructions
        };
    }

    /**
     * Checks if the current or future time is within deliverable hours for the given zone.
     *
     * @param {any} zoneStatus - The zone status object containing deliverable hours.
     * @returns {boolean} - Whether the address is within deliverable hours.
     */
    private readonly _isAddressDeliverable = (zoneStatus: IAddressZone): boolean => {
        const futureDate = this._order.getProperty("FutureDate");
        const futureMinute = this._order.getProperty("FutureMinute");

        if (futureDate) {
            return this._isDeliverableAtFutureTime(zoneStatus, futureDate, futureMinute);
        }

        return this._isDeliverableASAP(zoneStatus);
    }

    /**
     * Checks if the future time is within deliverable hours for the given zone.
     * @param {IAddressZone} zoneStatus -  The zone status object containing deliverable hours.
     * @param {Date} futureDate - Future order date
     * @param {number} futureMinute - Future order minutes
     * @returns  {boolean} - Whether the address is within deliverable hours.
     */
    private readonly _isDeliverableAtFutureTime = (zoneStatus: IAddressZone, futureDate: Date, futureMinute: number): boolean => {
        const futureWeekday = futureDate.getUTCDay();
        const futureSchedule = zoneStatus.LIMTIME.find(schedule => schedule.weekdayId === futureWeekday);

        if (futureSchedule) {
            const startsBefore = futureSchedule.startMinute < futureMinute;
            const endsAfter = futureMinute < futureSchedule.endMinute;
            if (startsBefore && endsAfter) {
                return true;
            }
        }

        return false;
    }

    /**
     * Checks if the current time is within deliverable hours for the given zone.
     * @param {IAddressZone} zoneStatus -  The zone status object containing deliverable hours.
     * @returns   {boolean} - Whether the address is within deliverable hours.
     */
    private readonly _isDeliverableASAP= (zoneStatus: IAddressZone): boolean => {
        const offset = this._data.getProperty("TimeOffset");
        const currentDate = Util.NowStore(offset);

        const currentWeekday = currentDate.getDay();
        const currentMinutes = currentDate.getHours() * 60 + currentDate.getMinutes();

        const currentSchedule = zoneStatus.LIMTIME.find(schedule => schedule.weekdayId === currentWeekday);

        if (currentSchedule) {
            const startsBefore = currentSchedule.startMinute < currentMinutes;
            const endsAfter = currentMinutes < currentSchedule.endMinute;
            if (startsBefore && endsAfter) {
                return true;
            }
        }

        return false;
    };

    /**
     * Checks for ZIP code exceptions where a Google ZIP code might differ from Adora's.
     *
     * @param {string} googleZip - The ZIP code from Google.
     * @param {string} adoraZip - The ZIP code from Adora.
     * @returns {string} - Returns the Adora ZIP if an exception is found, otherwise an empty string.
     */
    private readonly _zipException = (googleZip: string, adoraZip: string): string => {
        const zipExceptions: Record<string, string> = {
            "84009": "84095",
            "91350": "91390"
        };

        return zipExceptions[adoraZip] === googleZip ? adoraZip : "";
    }

    /**
     * Attaches the shared suggestion list to the street input, biased to the store or the
     * user's last known location. The chain behind it decides which provider answers.
     */
    private _initializeAddressSuggestBox(): void {
        const input = this._containerReferences.streetAddress;
        const chain = this._item.geoSearch.chain;
        if (!input || !chain?.hasSuggestProvider)
            return;

        this._addressSuggestBox?.dispose();
        this._addressSuggestBox = new AddressSuggestBox({
            input,
            chain,
            mode: "address",
            getBiasLocation: () => this._getAutocompleteBiasLocation(),
            getCountryCodes: () => this._getBrandCountryCodes(),
            getAnnouncement: () => this._localizationService.names("AutoSuggestionsAvailable"),
            onAddressSelected: address => this._handlePlaceSelectionAsync(address)
        });
    }

    /**
     * OTAT already knows which store the customer is ordering from, so it biases address
     * search to that store's coordinates. Other consumers (e.g. Locations, which has no
     * single store yet) keep biasing to the user's last known location.
     */
    private _getAutocompleteBiasLocation(): LatLng {
        if (this._isSingleStoreScoped) {
            const storeInfo = this._data.getProperty("StoreInfo");
            if (storeInfo?.latitude && storeInfo?.longitude)
                return { lat: storeInfo.latitude, lng: storeInfo.longitude };
        }

        return Util.getUserLocationFromCookie() ?? this._getBrandStoreLocation();
    }

    /**
     * Last resort when the customer has never shared their location: the brand's own store,
     * falling back to its country's centre. HERE rejects an autosuggest request that carries
     * no search centre at all, so this never returns null.
     */
    private _getBrandStoreLocation(): LatLng {
        const storeInfo = this._data.getBrandProperty("StoreInfo");
        if (storeInfo?.latitude && storeInfo?.longitude)
            return { lat: storeInfo.latitude, lng: storeInfo.longitude };

        return getCountryCentroid(storeInfo?.country?.IsoCode);
    }

    /**
     * Restricts suggestions to the countries the brand operates in, so a numeric query cannot
     * match a postal code on another continent.
     */
    private _getBrandCountryCodes(): string[] {
        return (this._data.getBrandProperty("Countries") ?? []).map(country => country.IsoCode);
    }

    /**
     * Handles the event when a place is selected.
     * Determines if reverse geocoding is needed (e.g., for specific countries like Mexico),
     * then calls a function to fill in the address with selected or overridden values.
     */
    private async _handlePlaceSelectionAsync(address: GeoAddress): Promise<void> {
        if (!address)
            return;

        // Reverse geocode if the country is Mexico to fetch the city.
        if (this._getCountryIdFromIso(address.countryCode) === Country.MEXICO)
            await this._fetchCityForMexicoFromCoordsAsync(address.coords);
        else
            await this._fillInSelectedAddressAsync(address);
    }

    /**
     * Fetches city for Mexico by performing reverse geocoding.
     * This is needed due to the unique structure of place data in Mexico.
     *
     * @param {LatLng} coords - The selected coords from Autocomplete.
     */
    private readonly _fetchCityForMexicoFromCoordsAsync = async (coords: LatLng): Promise<string | null> => {
        const addr = await this._item.geoSearch.chain?.reverseGeocodeAsync(coords);
        if (!addr) {
            DialogCreators.messageBoxOk(
                this._localizationService,
                this._dataLayerService,
                this._localizationService.names("SystemCannotLocateAddress"),
                true
            );
            return null;
        }

        // You can normalize the city rule here if Mexico needs a special fallback:
        // e.g. HERE might give city in district/county; Google might differ.
        return addr.city ?? null;
    };

    /**
     * Fills in the address fields based on the selected place from Google Maps Autocomplete.
     * If a cityOverride is provided, it will replace the city obtained from the place.
     *
     * @param {google.maps.places.PlaceResult} place - The place selected from Google Autocomplete.
     * @param {string | null} cityOverride - Optional city override for cases where geocoding is required.
     * @returns {Promise<boolean>} - Returns false if the address is outside the delivery zone.
     */
    private async _fillInSelectedAddressAsync(geo: GeoAddress | null, cityOverride: string | null = null): Promise<void> {
        Util.resetAnnounceCycle();
        if (!geo)
            return;

        this._hideAddressErrors();

        const { address, countryLong, stateFullName } = this._mapGeoAddressToTempAddress(geo, cityOverride);

        // Perform USPS validation only for incomplete US addresses
        if (address.CountryID === Country.UNITED_STATES && !this._isAddressComplete(address)) {
            const streetLine = this._buildUspsStreetLine(
                this._item.streetAddress,
                address.StreetName
            );

            const zip = address.Zip ?? "";
            const result = await this._addressService.validateAddressAsync(streetLine, zip);
            
            if (result.valid && result.normalizedAddress) {
                this._mapStreetAddressToGooglePlace(address, result.normalizedAddress);
                await this._geocodeNormalizedAddressAsync(address);
            }
        }

        // Update display 
        this._setDeliveryAddressDisplay(address, countryLong, stateFullName);

        // Find closest stores using lat/lng
        const closestStores = await this._getClosestStoresToAddressAsync(address.Latitude, address.Longitude);

        // If still incomplete, stop here and show error
        if (!this._isAddressComplete(address)) {
            Util.beginBatchAnnouncement();
            try {
                this._handleIncompleteAddress(address);
            } finally {
                Util.endBatchAnnouncement();
            }
            Util.announceErrors(Util.collectVisibleErrors(this._containerElement));
            return;
        }

        // Address is complete, check delivery zones
        this._checkIfAddressIsInZone(closestStores, address);
    }

    /**
     * Parses a GeoAddress into a temporary internal address model.
     *
     * Extracts street number, street name, city, state, postal code, country,
     * neighborhood, latitude, and longitude from Google's address components.
     * Applies optional city overrides and runs address normalization logic.
     *
     * @param {GeoAddress} addr - The GeoAddress object returned from Autocomplete or Place Details API.
     * @param {string | null} [cityOverride=null] - Optional city value that overrides Google's detected city.
     *
     * @returns {{
     *   address: ITempAddress,
     *   countryLong: string,
     *   stateFullName: string
     * }} Parsed address metadata, including the internal address model and the long-form state/country values.
     */
    private _mapGeoAddressToTempAddress(addr: GeoAddress, cityOverride: string | null = null): {
        address: ITempAddress,
        countryLong: string,
        stateFullName: string
    }
    {
        const result = {
            address: this._getTempAddressNew(),
            countryLong: addr.countryName ?? "",
            stateFullName: addr.stateName ?? ""
        };

        result.address.Latitude = addr.coords?.lat ?? 0;
        result.address.Longitude = addr.coords?.lng ?? 0;

        result.address.StreetNo = addr.houseNumber ?? "";
        result.address.StreetName = addr.street ?? "";
        result.address.City = cityOverride ?? addr.city ?? "";

        // Your existing fields (neighborhood / sublocality equivalents)
        // Pick a consistent mapping so both Google and HERE behave the same.
        result.address.Address3 = addr.district ?? "";
        result.address.Address4 = addr.sublocality ?? "";

        result.address.State = addr.stateCode ?? "";
        result.address.Zip = addr.postalCode ?? "";

        // CountryID from ISO code
        result.address.CountryID = this._getCountryIdFromIso(addr.countryCode);

        // Validate and adjust address fields
        this._validateAddressCity(result.address, result.address.Address3);
        this._validateAddressStreetNumber(result.address);

        return result;
    }

    /**
     * Builds a USPS-ready street line by merging the user-entered street number
     * with Google's full street name (route). This ensures accurate USPS validation
     * even when Google omits the street number or the user types only a partial street name.
     *
     * @param {string} rawInput - The full raw street line entered by the user (e.g., "817 Gateway Glob").
     * @param {string | null | undefined} googleStreetName - The full street name returned by Google (e.g., "Gateway Global Drive").
     *
     * @returns {string} A normalized street line suitable for USPS validation (e.g., "817 Gateway Global Drive").
     */
    private _buildUspsStreetLine(rawInput: string, googleStreetName: string | null | undefined): string {
        const cleanedInput = rawInput.trim();

        // Extract street number from user input
        const streetNumber = cleanedInput.match(/^\d+[A-Za-z]?/)?.[0] ?? "";
        const fullRoute = googleStreetName ?? "";

        // Merge: User number + Google route
        if (streetNumber && fullRoute)
            return `${streetNumber} ${fullRoute}`.trim();

        return cleanedInput;
    }

    /**
     * Merges USPS-normalized address data into the parsed Google address model.
     * Only fills fields that Google failed to provide (e.g., missing street number or city).
     *
     * @param {ITempAddress} address - The local address object being populated.
     * @param {AddressResponse} dbAddress - The USPS or database-normalized address result.
     *
     * @returns {void}
     */
    private _mapStreetAddressToGooglePlace(address: ITempAddress, dbAddress: AddressResponse): void {
        if (!address.StreetNo)
            address.StreetNo = dbAddress.streetNumber;

        if (!address.City)
            address.City = dbAddress.city;
    }

    /**
     * Geocodes a fully normalized address using Google Maps Geocoding API.
     *
     * This method should be called after USPS or other normalization logic
     * has filled in missing address components (e.g., street number or city).
     * It updates the address latitude and longitude in-place when geocoding
     * succeeds. If geocoding fails or no geocoder is available, the method
     * exits silently without throwing.
     *
     * @param {ITempAddress} address - The normalized address object to geocode.
     *
     * @returns {Promise<void>} A promise that resolves once geocoding completes
     * (successfully or not). The address object may be mutated with updated
     * latitude and longitude values.
     */
    private async _geocodeNormalizedAddressAsync(address: ITempAddress): Promise<void> {
        const chain = this._item.geoSearch.chain;
        if (!chain?.hasGeocodingProvider)
            return;

        const fullAddress = this._buildGeocodeAddress(address);

        const coords = await chain.geocodeAsync(fullAddress);
        if (!coords)
            return;

        address.Latitude = coords.lat;
        address.Longitude = coords.lng;
    }

    /**
     * Builds a single-line address string suitable for geocoding.
     *
     * Combines individual address components into a normalized string format
     * expected by the Google Maps Geocoding API. Empty or undefined components
     * are automatically excluded.
     *
     * Example output:
     * "817 Gateway Global Drive Byhalia MS 38611"
     *
     * @param {ITempAddress} address - The address object containing individual
     * address components.
     *
     * @returns {string} A formatted address string for geocoding.
     */
    private _buildGeocodeAddress(address: ITempAddress): string {
        return [
            address.StreetNo,
            address.StreetName,
            address.City,
            address.State,
            address.Zip
        ]
            .filter(Boolean)
            .join(" ");
    }

    /**
     * Handles cases where the parsed address is incomplete or invalid.
     * Clears selected store/address state, displays an error, and notifies
     * external handlers via the selection callback.
     *
     * @param {ITempAddress} address - The incomplete address instance to report.
     *
     * @returns {void}
     */
    private _handleIncompleteAddress(address: ITempAddress): void {
        this._item.selectedStoreKey = "";
        this._item.selectedAddress = null;
        this._showIncompleteAddressError();
        InputValidator.trackValidationErrorByLtag("DeliveryAddressIncompleteAddressError", this._localizationService, this._dataLayerService);

        this._selectAddressFunc(AddressValidation.INVALID, "", address);
    }

    private _showIncompleteAddressError(): void {
        const errorId = `spn_${this._item.locationId}_delivery_address_error`;
        ValidationDOMHelper.applyErrorState(this._containerReferences.streetAddress, errorId);
    }

    /**
     * Ensures that the city field is set correctly by checking against known neighborhoods.
     *
     * @param {IAddressFormat} address - The address object being validated.
     * @param {string} neighborhood - The neighborhood name.
     * @param {string | null} cityOverride - Optional city override value.
     */
    private readonly _validateAddressCity = (address: ITempAddress, neighborhood: string): void => {
        if (address.City || !neighborhood)
            return;

        const allCities = this._item.locations.flatMap(x => x.cities);
        if (allCities.includes(neighborhood))
            address.City = neighborhood;
    }

    /**
     * Validates the street number of the address and attempts to fill it if it's missing.
     *
     * @param {IAddressFormat} address - The address object being validated.
     */
    private readonly _validateAddressStreetNumber = (address: ITempAddress): void => {
        if (address.StreetNo || address.StreetNo === "null")
            return;

        const parsedAddress = this._parseAddress(this._item.streetAddress);
        if (parsedAddress.StreetNo)
            address.StreetNo = parsedAddress.StreetNo;
    }

    /**
     * Sets the delivery address display in the input field based on the selected address.
     *
     * @param {IAddressFormat} address - The address object to display.
     * @param {string} countryLong - The full country name.
     * @param {string} stateFullName - The full state name (for specific formatting).
     */
    private readonly _setDeliveryAddressDisplay = (address: ITempAddress, countryLong: string, stateFullName: string): void => {
        const input = this._containerReferences.streetAddress;
        if (!input)
            return;

        if (address.CountryID !== 2)
            input.value = `${address.StreetNo} ${address.StreetName}, ${address.City}, ${address.State} ${address.Zip}`;
        else // Special format for Mexico
            input.value = `${address.StreetName} ${address.StreetNo}, ${address.Address4}, ${address.City}, ${address.Zip} ${stateFullName}, ${address.State}, ${countryLong}`;
    }

    /**
     * Retrieves the country ID from the provided ISO code.
     * Searches through the address components to find the country ISO code and
     * matches it with known country data to return the corresponding country ID.
     *
     * @param {string | null} isoCode - The ISO Code.
     * @returns {number} - The country ID if found, or -1 if not found.
     */
    private _getCountryIdFromIso(isoCode?: string | null): number {
        const iso = isoCode?.toLowerCase().trim();
        if (!iso)
            return -1;

        // Bahamas correction (keep your business rule)
        const normalizedIso = iso === "bs" ? "bh" : iso;

        const country = this._data
            .getBrandProperty("Countries")
            .find(x => x.IsoCode.toLowerCase().trim() === normalizedIso);

        return country ? country.CountryID : -1;
    }

    private _parseAddress(address: string): IAddressParsed {
        const zips = this._data.getBrandProperty("ZipCodes").split(",");
        const cities = this._data.getBrandProperty("Cities").split(",") || [];
        const stateData = this._data.getBrandProperty("Settings").STAT || "";
        const addrZip = this._parseAddressGetZip(address, zips);
        const addrState = this._parseAddressGetState(addrZip.Address, stateData);
        const addrCity = this._parseAddressGetCity(addrState.Address, cities);

        let iAddress = addrCity.Address;
        let zip = addrZip.Zip;
        let state = addrState.State;
        let city = addrCity.City;

        const streetDirAbbr = ["n", "ne", "nw", "s", "se", "sw", "e", "w"];
        const stSuffixAbbr = ["aly", "anx", "arc", "ave", "bch", "bg", "blf", "blfs", "blvd", "bnd", "br", "brg", "brk", "brks", "btm", "byp", "byu",
            "cir", "cirs", "clb", "clf", "clfs", "cmn", "cmns", "cor", "cors", "cp", "cpe", "cres", "crk", "crse", "crst", "cswy", "ct", "ctr", "cts",
            "curv", "cv", "cvs", "cyn", "dl", "dm", "dr", "drs", "dv", "est", "ests", "expy", "ext", "exts", "fall", "fld", "flds", "fls", "flt", "flts",
            "frd", "frg", "frk", "frks", "frst", "fry", "ft", "fwy", "gdn", "gdns", "gln", "grn", "grns", "grv", "grvs", "gtwy", "hbr", "hl", "hls", "holw",
            "hts", "hvn", "hwy", "inlt", "is", "isle", "jct", "knl", "knls", "ky", "kys", "land", "lck", "lcks", "ldg", "lf", "lgt", "lgts", "lk", "lks",
            "ln", "lndg", "loop", "mall", "mdw", "mdws", "mews", "ml", "mls", "mnr", "msn", "mt", "mtn", "mtwy", "nck", "opas", "orch", "oval", "park",
            "pass", "path", "pike", "pkwy", "pl", "pln", "plns", "plz", "pne", "pnes", "pr", "prt", "psge", "pt", "pts", "radl", "ramp", "rd", "rdg",
            "rdgs", "rds", "riv", "rnch", "row", "rpds", "rst", "rte", "rue", "run", "shl", "shls", "shr", "shrs", "skwy", "smt", "spg", "spgs", "spur",
            "sq", "sqs", "st", "sta", "stra", "strm", "sts", "ter", "tpke", "trak", "trce", "trfy", "trl", "trlr", "trwy", "tunl", "un", "upas", "via",
            "vis", "vl", "vlg", "vlgs", "vly", "vw", "vws", "walk", "wall", "way", "ways", "wl", "wls", "xing", "xrd", "xrds"];
        const addr = iAddress.split("  ").join(" ").trim().split(" ").join("^").toLowerCase();
        let stNo = isNaN(parseInt(addr)) ? "" : parseInt(addr).toString();
        let stPostFix = "th";
        const stNoRight = Util.RightText(stNo, 1);
        if (stNoRight === "1") {
            stPostFix = "st";
        } else if (stNoRight === "2") {
            stPostFix = "nd";
        } else if (stNoRight === "3") {
            stPostFix = "rd";
        }
        const tmpStName = stNo + stPostFix;
        if (addr.substring(0, tmpStName.length) === tmpStName) {
            stNo = "";
        }
        const Address = {
            Address: "",
            StNo: stNo,
            StPreDirAbbr: "",
            StPostDirAbbr: "",
            StSuffixAbbr: "",
            StName: "",
        };
        const stName = addr.substring(stNo.length + 1).split("^");
        if (stName.length === 1) {
            Address.StName = Util.FixStringCase(stName[0]);
        } else {
            let sIndex = 0;
            let eIndex = stName.length - 1;
            let lookIn = stName[sIndex];
            for (const abbr of streetDirAbbr) {
                if (abbr === lookIn) {
                    Address.StPreDirAbbr = lookIn.toUpperCase();
                    sIndex += 1;
                    break;
                }
            }
            if (sIndex === eIndex) {
                Address.StName = stName[sIndex];
            } else {
                lookIn = stName[eIndex];
                for (const abbr of streetDirAbbr) {
                    if (abbr === lookIn) {
                        Address.StPostDirAbbr = lookIn.toUpperCase();
                        eIndex -= 1;
                        break;
                    }
                }
                if (sIndex === eIndex) {
                    Address.StName = stName[sIndex];
                } else {
                    lookIn = stName[eIndex];
                    for (const abbr of stSuffixAbbr) {
                        if (abbr === lookIn) {
                            Address.StSuffixAbbr = Util.FixStringCase(lookIn);
                            eIndex -= 1;
                            break;
                        }
                    }
                    for (let i = sIndex; i <= eIndex; i++) {
                        Address.StName += stName[i] + " ";
                    }
                }
            }
            Address.StName = Util.FixStringCase(Address.StName);
        }
        let tempAddr = "";
        if (Address.StPreDirAbbr !== "") tempAddr += Address.StPreDirAbbr + " ";
        if (Address.StName !== "") tempAddr += Address.StName + " ";
        if (Address.StSuffixAbbr !== "") tempAddr += Address.StSuffixAbbr + " ";
        if (Address.StPostDirAbbr !== "") tempAddr += Address.StPostDirAbbr + " ";
        Address.Address = tempAddr;

        const addre = {
            StreetNo: Address.StNo.trim(),
            StreetName: tempAddr.trim(),
            StreetShortName: tempAddr.trim(),
            City: city.trim(),
            State: state.trim(),
            Zip: zip.trim(),
            Latitude: 0,
            Longitude: 0
        };

        return addre;
    }

    public _parseAddressGetZip(address: string, zips: string[]): IParseAddressGetZip {
        address = address.trim();
        const addr: IParseAddressGetZip = {
            Zip: "",
            Address: ""
        };

        try {
            let iZip = "";
            let idx = -1;
            for (const zip of zips) {
                idx = address.lastIndexOf(zip);
                if (idx > 4) {
                    iZip = zip;
                    break;
                }
            }
            if (idx > 4) {
                if (idx > 4) {
                    addr.Zip = iZip;
                    const part1 = address.substring(0, idx);
                    const part2 = address.substring(idx);
                    let tempAddr = part1 + " " + part2.replace(iZip, "");
                    tempAddr = tempAddr.replaceAll("  ", " ");
                    tempAddr = tempAddr.replaceAll("  ", " ");
                    addr.Address = tempAddr.trim();
                } else {
                    addr.Zip = "";
                    addr.Address = address.trim();
                }
            }
        } catch (ex: unknown) {
            //if (ex instanceof Error)
            //    Util.LogError("ParseAddressGetZip", ex, globalThis.aOLO);

            addr.Zip = "";
            addr.Address = address.trim();
        }
        return addr;
    }

    private _parseAddressGetState(address: string, state: string): IParseAddressGetState {
        address = address.trim();
        const addr: IParseAddressGetState = {
            State: "",
            Address: ""
        };
        try {
            const idx = address.toUpperCase().lastIndexOf(" " + state + " ");
            if (idx > -1) {
                addr.State = state;
                const part1 = address.substring(0, idx);
                const part2 = address.substring(idx);
                let tempAddr = part1 + " " + part2.replace(state, "");
                tempAddr = tempAddr.replaceAll("  ", " ");
                tempAddr = tempAddr.replaceAll("  ", " ");
                addr.Address = tempAddr;
            } else if (address.endsWith(" " + state)) {
                addr.State = state;
                addr.Address = address.substring(0, address.length - 2).trim();
            } else {
                addr.State = "";
                addr.Address = address.trim();
            }
        } catch (ex: unknown) {
            //if (ex instanceof Error)
            //    Util.LogError("ParseAddressGetState", ex, globalThis.aOLO);

            addr.State = "";
            addr.Address = address.trim();
        }
        return addr;
    }

    private _parseAddressGetCity(address: string, cities: string[]): IParseAddressGetCity {
        address = address.trim();
        const addr: IParseAddressGetCity = {
            Address: "",
            City: ""
        };

        try {
            let iCity = "";
            let idx = -1;
            for (const c of cities) {
                const city = c.toUpperCase().replaceAll("'", "").trim();

                idx = address.toUpperCase().lastIndexOf(city);
                if (idx > 4) {
                    iCity = c.replaceAll("'", "").trim();
                    break;
                }
            }
            if (idx > 4) {
                addr.City = iCity;
                const part1 = address.substring(0, idx);
                const part2 = address.substring(idx);
                let tempAddr = part1 + " " + part2.substring(iCity.length);
                tempAddr = tempAddr.replaceAll("  ", " ");
                tempAddr = tempAddr.replaceAll("  ", " ");
                addr.Address = tempAddr.trim();
            } else {
                addr.City = "";
                addr.Address = address.trim();
            }
        } catch (ex: unknown) {
            //if (ex instanceof Error)
            //    Util.LogError("ParseAddressGetCity", ex, globalThis.aOLO);

            addr.City = "";
            addr.Address = address.trim();
        }
        return addr;
    }

    /**
     * Maps a provider-neutral GeoAddress into an internal IAddressParsed object.
     * Applies city and street-number fallback rules when provider data is incomplete.
     *
     * @param {GeoAddress | null} geo - Provider-neutral geocoded address data (may be null).
     * @param {string} streetNoFallback - Street number to use when provider data is missing.
     * @param {string} cityFallback - City to use as a final fallback when no match is found.
     * @param {string[]} cities - List of known store cities used for neighborhood matching.
     * @returns {IAddressParsed} Parsed address with normalized fields and applied fallbacks.
     */
    private _parseGeoAddress(
        geo: GeoAddress | null,
        streetNoFallback: string,
        cityFallback: string,
        cities: string[]
    ): IAddressParsed {
        const address: IAddressParsed = {
            StreetNo: geo?.houseNumber ?? "",
            StreetName: geo?.street ?? "",
            StreetShortName: geo?.streetShortName ?? "",
            City: geo?.city ?? "",
            State: geo?.stateCode ?? "",
            Zip: geo?.postalCode ?? "",
            Latitude: geo?.coords.lat ?? 0,
            Longitude: geo?.coords.lng ?? 0
        };

        this._applyCityFallback(address, geo, cityFallback, cities);
        this._applyStreetNoFallback(address, streetNoFallback);

        return address;
    }

    /**
     * Applies city fallback logic when the parsed address has no city.
     * Resolution order:
     * 1) Explicit city fallback if it matches provider neighborhood data
     * 2) Match against known store cities using neighborhood data
     * 3) Final explicit city fallback
     *
     * @param {IAddressParsed} address - Address object to mutate.
     * @param {GeoAddress | null} geo - Provider-neutral geocode result.
     * @param {string} cityFallback - Explicit city fallback value.
     * @param {string[]} cities - List of known store cities for matching.
     * @returns {void}
     */
    private _applyCityFallback(
        address: IAddressParsed,
        geo: GeoAddress | null,
        cityFallback: string,
        cities: string[]
    ): void {
        if (!this._isBlank(address.City))
            return;

        const neighborhoodCandidates = [
            geo?.district?.trim(),
            geo?.sublocality?.trim()
        ].filter(Boolean) as string[];

        // Prefer explicitly provided city if it matches neighborhood
        if (cityFallback && neighborhoodCandidates.includes(cityFallback)) {
            address.City = cityFallback;
            return;
        }

        // Match against known store cities
        const matchedCity = cities
            .map(c => c.replaceAll("'", "").trim())
            .find(c => neighborhoodCandidates.includes(c));

        if (matchedCity) {
            address.City = matchedCity;
            return;
        }

        // Final fallback
        if (cityFallback)
            address.City = cityFallback;
    }

    /**
     * Applies a street number fallback when the parsed address street number is blank.
     *
     * @param {IAddressParsed} address - Address object to mutate.
     * @param {string} streetNoFallback - Street number fallback value.
     * @returns {void}
     */
    private _applyStreetNoFallback(address: IAddressParsed, streetNoFallback: string): void {
        if (this._isBlank(address.StreetNo) && streetNoFallback)
            address.StreetNo = streetNoFallback;
    }

    /**
     * Determines whether a value should be treated as blank.
     * Treats empty strings, null, undefined, and the string "null" as blank.
     *
     * @param {unknown} value - Value to evaluate.
     * @returns {boolean} True if the value is considered blank.
     */
    private _isBlank(value: unknown): boolean {
        return value === "" || value === null || value === undefined || value === "null";
    }
}
